// Composable behavior for Stimulus controllers that persist UI state.
//
// Wraps lib/persisted_state and wires cross-tab synchronization into the
// controller lifecycle, removing manual localStorage juggling.
//
// Usage inside a controller:
//
//   import { usePersistedState } from "../mixins/persistable"
//
//   connect() {
//     this.state = usePersistedState(this, "chatDock", { collapsed: true })
//     this.collapsedValue = this.state.get("collapsed")
//   }
//
//   toggle() {
//     this.collapsedValue = !this.collapsedValue
//     this.state.set("collapsed", this.collapsedValue)
//   }
//
// Any subscribe() listeners registered through the returned object are removed
// automatically when the controller disconnects.

import { persistedState } from "../lib/persisted_state"

export function usePersistedState(controller, namespace, defaults = {}) {
  const store = persistedState(namespace, defaults)
  const unsubscribers = []

  const wrapped = {
    get: (key) => store.get(key),
    set: (key, value) => store.set(key, value),
    remove: (key) => store.remove(key),
    subscribe: (key, callback) => {
      const off = store.subscribe(key, callback)
      unsubscribers.push(off)
      return off
    },
  }

  if (!controller._persistTeardownInstalled) {
    controller._persistTeardownInstalled = true
    const originalDisconnect = controller.disconnect?.bind(controller)
    controller.disconnect = () => {
      unsubscribers.forEach((off) => off?.())
      unsubscribers.length = 0
      originalDisconnect?.()
    }
  }

  return wrapped
}

export default usePersistedState
